// Area: access (owner admin-ops, plan §9.19): audit log, license, test access.
import { z } from 'zod';
import {
  AccessExplainDTO,
  AccessExplainInput,
  AuditEventDetailDTO,
  AuditFacetsDTO,
  AuditPageDTO,
  AuditQuery,
  AuditSavedFilterDTO,
  EffectiveAccessDTO,
  LicensePreviewDTO,
  LicenseKeyInput,
  InstallationOperatorsDTO,
  InstanceDTO,
  SaveAuditFilterInput,
} from '@dataroom/core/access';
import { defineContract } from '../define';

export { AuditEventDetailDTO, AuditFacetsDTO, AuditPageDTO, AuditQuery, AuditSavedFilterDTO };

export { EffectiveAccessDTO };

export const effectiveAccessContract = defineContract({
  method: 'GET',
  path: '/access/me',
  response: EffectiveAccessDTO,
  summary: 'Effective object permissions of the caller (view/create/edit/delete/export)',
});

const auditRead = 'audit.read';

export const auditListContract = defineContract({
  method: 'GET',
  path: '/audit',
  query: AuditQuery,
  response: AuditPageDTO,
  permission: auditRead,
  summary: 'Audit events (newest first, keyset pagination) with filters',
});

export const auditFacetsContract = defineContract({
  method: 'GET',
  path: '/audit/facets',
  response: AuditFacetsDTO,
  permission: auditRead,
  summary: 'Actors, categories and objects for the audit filters',
});

export const auditEventContract = defineContract({
  method: 'GET',
  path: '/audit/:id',
  params: z.object({ id: z.uuid() }),
  response: AuditEventDetailDTO,
  permission: auditRead,
  summary: 'Audit event with typed diff, context, its correlation chain and what happened next',
});

export const auditSavedFiltersContract = defineContract({
  method: 'GET',
  path: '/audit/saved-filters',
  response: z.array(AuditSavedFilterDTO),
  permission: auditRead,
  summary: 'Saved audit filters of the workspace',
});

export const saveAuditFilterContract = defineContract({
  method: 'POST',
  path: '/audit/saved-filters',
  body: SaveAuditFilterInput,
  response: z.array(AuditSavedFilterDTO),
  permission: auditRead,
  summary: 'Save the current audit filter under a name (a filter of the same name is replaced)',
});

export const deleteAuditFilterContract = defineContract({
  method: 'DELETE',
  path: '/audit/saved-filters/:id',
  params: z.object({ id: z.uuid() }),
  response: z.array(AuditSavedFilterDTO),
  permission: auditRead,
  summary: 'Delete a saved audit filter',
});

// -- installation: license and updates (instance operators) ---------------------------------------

export const instanceSettingsContract = defineContract({
  method: 'GET',
  path: '/settings/instance',
  response: InstanceDTO,
  permission: 'workspace.admin',
  summary: 'License, seats and updates of the installation (instance operators only)',
});

export const installationOperatorsContract = defineContract({
  method: 'GET',
  path: '/settings/instance/operators',
  response: InstallationOperatorsDTO,
  permission: 'workspace.admin',
  summary: 'The operators of the installation (administrators of the workspace it was set up with), by name',
});

export const licensePreviewContract = defineContract({
  method: 'POST',
  path: '/settings/general/license/preview',
  body: LicenseKeyInput,
  response: LicensePreviewDTO,
  permission: 'settings.manage',
  summary:
    'Parse and verify a license key without applying it (instance operators only; edition, seats, expiry, problems)',
});

export const licenseApplyContract = defineContract({
  method: 'PUT',
  path: '/settings/general/license',
  body: LicenseKeyInput,
  response: InstanceDTO,
  permission: 'settings.manage',
  summary: 'Replace the license key of the installation (instance operators only, audited)',
});

// -- test access (Roles › View as) ---------------------------------------------------------------

export const accessExplainContract = defineContract({
  method: 'POST',
  path: '/access/explain',
  body: AccessExplainInput,
  response: AccessExplainDTO,
  permission: 'settings.read',
  summary: 'Test access: may a member view, edit or delete one record, and why (role level and record rule)',
});
export * from './modules';
